import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, Quote, BadgeCheck, MapPin } from "lucide-react";
import PageHero from "../components/PageHero";
import { Reveal, Marquee } from "../components/motion";
import { ADDRESS, ORAI_BRANCH } from "../lib/site";

export default function About() {
  return (
    <main data-testid="about-page">
      <PageHero
        eyebrow="About PocketBima"
        title="Protection should fit"
        highlight="everyone's pocket."
        subtitle="PocketBima is a digital-first insurance assistance brand from Greater Noida West, built around one simple promise — Chhoti Pocket, Badi Suraksha."
        image="/images/about.webp"
      />

      <Marquee items={["Simple", "Accessible", "Affordable", "For Every Indian Family", "Genuine Protection"]} />

      {/* FOUNDER'S MESSAGE */}
      <section className="relative overflow-hidden bg-ice py-24 lg:py-32" data-testid="founder-section">
        <div className="pointer-events-none absolute -left-32 top-10 h-96 w-96 rounded-full bg-mint/10 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 md:px-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20">
          <Reveal>
            <div className="relative mx-auto max-w-md">
              <div className="pointer-events-none absolute -inset-6 rounded-[3rem] bg-gradient-to-br from-skycyan/15 to-mint/15 blur-2xl" />
              <div className="relative overflow-hidden rounded-[2.5rem] border border-navy/10 shadow-lift" data-testid="founder-photo-frame">
                <img
                  src="/images/founder.webp"
                  alt="Gaurav Singh, Founder of Pocket Bima Insurance Service"
                  width="640"
                  height="640"
                  loading="lazy"
                  decoding="async"
                  className="aspect-[4/4.6] w-full object-cover"
                  data-testid="founder-photo"
                />
              </div>
              <div className="animate-float-soft absolute -right-4 bottom-8 flex items-center gap-3 rounded-2xl border border-navy/10 bg-white/95 px-5 py-4 shadow-lift backdrop-blur-md" data-testid="founder-experience-badge">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy">
                  <BadgeCheck className="h-6 w-6 text-mint" />
                </span>
                <span>
                  <span className="block font-display text-sm font-extrabold text-navy">7+ Years in Insurance</span>
                  <span className="block text-xs font-medium text-charcoal/60">Motor Insurance Specialist</span>
                </span>
              </div>
            </div>
          </Reveal>

          <div>
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-skycyan-600">Founder's Message</p>
              <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl" data-testid="founder-heading">
                "Insurance should never feel out of reach."
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Quote className="mt-8 h-8 w-8 text-mint" fill="currentColor" strokeWidth={0} />
              <div className="mt-5 space-y-5 text-base leading-relaxed text-charcoal/85 md:text-lg" data-testid="founder-message">
                <p>
                  Namaste, I'm Gaurav. For over seven years, I've worked in the insurance
                  industry — most of it specialising in motor insurance — sitting across from
                  everyday Indians trying to make sense of policies, premiums and paperwork.
                </p>
                <p>
                  Again and again, I saw the same thing: insurance felt confusing, expensive
                  and inaccessible for the very families who needed protection the most. People
                  weren't avoiding insurance — they were avoiding the complexity around it.
                </p>
                <p className="font-semibold text-navy">
                  I started PocketBima to change that. Honest guidance, clear language and
                  protection that genuinely fits your pocket — when you reach out to us, you're
                  talking to someone who treats your family's security like his own.
                </p>
              </div>
              <div className="mt-8 border-l-4 border-mint pl-5" data-testid="founder-signature">
                <p className="font-display text-xl font-extrabold text-navy">Gaurav Singh</p>
                <p className="text-sm font-medium text-charcoal/60">Founder, PocketBima</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MISSION & VISION */}
      <section className="relative overflow-hidden bg-navy py-24 lg:py-28" data-testid="mission-vision-section">
        <div className="pointer-events-none absolute -right-24 top-0 h-80 w-80 rounded-full bg-skycyan/10 blur-3xl" />
        <div className="pointer-events-none absolute -left-24 bottom-0 h-80 w-80 rounded-full bg-mint/10 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 md:px-12">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-mint">What Drives Us</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl" data-testid="mission-vision-heading">
              Our Mission & Vision
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            <Reveal delay={0.1}>
              <div className="group h-full rounded-3xl border border-white/10 bg-white/5 p-9 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1.5 hover:border-mint/30 sm:p-11" data-testid="mission-card">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-mint/15">
                  <Target className="h-7 w-7 text-mint" strokeWidth={1.9} />
                </span>
                <h3 className="mt-6 font-display text-2xl font-bold text-white">Our Mission</h3>
                <p className="mt-3 text-base leading-relaxed text-white/70">
                  Make insurance simple, accessible and affordable for every Indian family —
                  cutting through the jargon to bring genuine protection to every pocket.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="group h-full rounded-3xl border border-white/10 bg-white/5 p-9 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1.5 hover:border-skycyan/40 sm:p-11" data-testid="vision-card">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-skycyan/15">
                  <Eye className="h-7 w-7 text-skycyan-400" strokeWidth={1.9} />
                </span>
                <h3 className="mt-6 font-display text-2xl font-bold text-white">Our Vision</h3>
                <p className="mt-3 text-base leading-relaxed text-white/70">
                  To become India's most trusted digital insurance companion — where every
                  individual and family feels secure, regardless of their income or background.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* OUR OFFICES */}
      <section className="bg-ice py-20" data-testid="about-offices-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 md:px-12">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-skycyan-600">Visit Us</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl" data-testid="about-offices-heading">
              Our Offices
            </h2>
            <p className="mt-5 max-w-3xl text-base leading-relaxed text-charcoal/80">
              Pocket Bima Insurance Service is based in Greater Noida West, Uttar Pradesh, with a
              branch office in Orai. We work with individuals, families, vehicle owners, travellers
              and businesses across the region — helping them understand motor, health, life, term,
              travel and business insurance in simple language before they buy. There is no charge
              for an enquiry: tell us what you want to protect, and a real insurance professional
              will explain your options, coverage and important terms, so you can decide with
              confidence. Reach us in person, on call at +91 95980 63001, or on WhatsApp.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {[
              { label: "Head Office", address: ADDRESS, testid: "office-hq" },
              { label: "Branch Office (Orai)", address: ORAI_BRANCH.address, testid: "office-orai" },
            ].map((o, i) => (
              <Reveal key={o.label} delay={i * 0.08}>
                <div className="flex h-full items-start gap-4 rounded-3xl border border-navy/10 bg-white p-8 shadow-soft transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lift" data-testid={o.testid}>
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-skycyan/15 to-mint/15">
                    <MapPin className="h-6 w-6 text-skycyan-600" strokeWidth={1.9} />
                  </span>
                  <div>
                    <h3 className="font-display text-lg font-bold text-navy">{o.label}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-charcoal/75">{o.address}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20" data-testid="about-cta-section">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-5 sm:px-8 md:px-12 lg:flex-row lg:items-center">
          <Reveal>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Ready to protect what matters?
            </h2>
            <p className="mt-3 max-w-lg text-charcoal/75">
              Tell us your requirement and our team will help you understand your options.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/contact"
              className="group flex items-center gap-2 rounded-full bg-mint px-8 py-4 text-base font-bold text-white shadow-mint transition-all duration-200 hover:-translate-y-0.5 hover:bg-mint-600"
              data-testid="about-cta-button"
            >
              Get a Free Quote <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
